/**
 * useBinomFlowDraft — local draft for the Binom Standard tab (drawer V2).
 * Wraps the unified Flow + the raw campaign detail into a mutable draft.
 * Exposes patchers + structural ops (paths + landers + offers) all rooted in
 * the same draft so dirty/reset works across edits.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Flow } from '@/hooks/voluum/useVoluum';
import type { BinomCampaignDetailRaw } from '@/hooks/binom';

import type {
  BinomCampaignMetaDraft,
  BinomDraftItem,
  BinomDraftPath,
  BinomFlowDraft,
} from './binom-flow-draft.types';
import {
  liveToDraft,
  snapshotForDirty,
  type DraftMutator,
} from './draft-internals';
import {
  addDefaultPath,
  addGroupPath,
  addLander,
  addOffer,
  moveDefaultPath,
  moveGroupPath,
  moveLander,
  moveOffer,
  removeDefaultPath,
  removeGroupPath,
  removeLander,
  removeOffer,
  type PathLocator,
} from './draft-structural';

export interface UseBinomFlowDraftResult {
  draft: BinomFlowDraft | null;
  patch: (mutator: DraftMutator) => void;
  patchDefaultPath: (idx: number, mutator: (p: BinomDraftPath) => void) => void;
  patchGroupPath: (
    groupIdx: number,
    pathIdx: number,
    mutator: (p: BinomDraftPath) => void,
  ) => void;
  patchCampaignMeta: (mutator: (m: BinomCampaignMetaDraft) => void) => void;
  addDefaultPath: () => void;
  removeDefaultPath: (idx: number) => void;
  moveDefaultPath: (from: number, to: number) => void;
  addGroupPath: (groupIdx: number) => void;
  removeGroupPath: (groupIdx: number, pathIdx: number) => void;
  moveGroupPath: (groupIdx: number, from: number, to: number) => void;
  addLanderTo: (loc: PathLocator, item: BinomDraftItem) => void;
  removeLanderFrom: (loc: PathLocator, idx: number) => void;
  moveLanderIn: (loc: PathLocator, from: number, to: number) => void;
  addOfferTo: (loc: PathLocator, item: BinomDraftItem) => void;
  removeOfferFrom: (loc: PathLocator, idx: number) => void;
  moveOfferIn: (loc: PathLocator, from: number, to: number) => void;
  reset: () => void;
  dirty: boolean;
}

export function useBinomFlowDraft(
  live: Flow | undefined,
  liveCampaign?: BinomCampaignDetailRaw | null,
): UseBinomFlowDraftResult {
  const [draft, setDraft] = useState<BinomFlowDraft | null>(() =>
    liveToDraft(live, liveCampaign),
  );
  const liveRef = useRef<Flow | undefined>(live);
  const liveCampaignRef = useRef<BinomCampaignDetailRaw | null | undefined>(
    liveCampaign,
  );

  useEffect(() => {
    const flowChanged = live && live !== liveRef.current;
    const campaignChanged = liveCampaign !== liveCampaignRef.current;
    if (flowChanged || campaignChanged) {
      liveRef.current = live;
      liveCampaignRef.current = liveCampaign;
      setDraft(liveToDraft(live, liveCampaign));
    }
  }, [live, liveCampaign]);

  const patch = useCallback((mutator: DraftMutator) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const copy = JSON.parse(JSON.stringify(prev)) as BinomFlowDraft;
      mutator(copy);
      return copy;
    });
  }, []);

  const reset = useCallback(() => {
    setDraft(liveToDraft(liveRef.current, liveCampaignRef.current));
  }, []);

  const dirty = useMemo(() => {
    if (!draft || !live) return false;
    return (
      snapshotForDirty(draft) !==
      snapshotForDirty(liveToDraft(live, liveCampaign)!)
    );
  }, [draft, live, liveCampaign]);

  const make = useCallback(
    <Args extends unknown[]>(fn: (d: BinomFlowDraft, ...args: Args) => void) =>
      (...args: Args) =>
        patch((d) => fn(d, ...args)),
    [patch],
  );

  return {
    draft,
    patch,
    patchDefaultPath: useCallback(
      (idx, m) =>
        patch((d) => {
          const t = d.defaultPaths[idx];
          if (t) m(t);
        }),
      [patch],
    ),
    patchGroupPath: useCallback(
      (g, p, m) =>
        patch((d) => {
          const t = d.pathGroups[g]?.paths[p];
          if (t) m(t);
        }),
      [patch],
    ),
    patchCampaignMeta: useCallback(
      (m) => patch((d) => m(d.campaignMeta)),
      [patch],
    ),
    addDefaultPath: useCallback(() => patch(addDefaultPath), [patch]),
    removeDefaultPath: useMemo(() => make(removeDefaultPath), [make]),
    moveDefaultPath: useMemo(() => make(moveDefaultPath), [make]),
    addGroupPath: useMemo(() => make(addGroupPath), [make]),
    removeGroupPath: useMemo(() => make(removeGroupPath), [make]),
    moveGroupPath: useMemo(() => make(moveGroupPath), [make]),
    addLanderTo: useMemo(() => make(addLander), [make]),
    removeLanderFrom: useMemo(() => make(removeLander), [make]),
    moveLanderIn: useMemo(() => make(moveLander), [make]),
    addOfferTo: useMemo(() => make(addOffer), [make]),
    removeOfferFrom: useMemo(() => make(removeOffer), [make]),
    moveOfferIn: useMemo(() => make(moveOffer), [make]),
    reset,
    dirty,
  };
}
